import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import api from '../lib/api';

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  const checkAuth = useCallback(async () => {
    try {
      const { data } = await api.get('/auth/me');
      setUser(data);
      localStorage.setItem('user', JSON.stringify(data));
    } catch {
      setUser(null);
      localStorage.removeItem('user');
      localStorage.removeItem('token');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    const saved = localStorage.getItem('user');
    if (saved) {
      try { setUser(JSON.parse(saved)); } catch {}
    }
    checkAuth();
  }, [checkAuth]);

  // Periodic auto-refresh: pull /auth/me every 60s so admin-side changes
  // (halt re-assignment, role updates, profile edits) propagate to live
  // sessions without requiring a manual sign-out.  Pauses while the tab is
  // hidden to save battery, and refreshes immediately on tab refocus.
  useEffect(() => {
    if (!user) return undefined;
    let intervalId = null;
    const start = () => {
      if (intervalId) return;
      intervalId = setInterval(() => { checkAuth(); }, 60000);
    };
    const stop = () => {
      if (intervalId) { clearInterval(intervalId); intervalId = null; }
    };
    const onVisibility = () => {
      if (document.hidden) {
        stop();
      } else {
        // Refresh once and resume polling.
        checkAuth();
        start();
      }
    };
    if (!document.hidden) start();
    document.addEventListener('visibilitychange', onVisibility);
    return () => {
      stop();
      document.removeEventListener('visibilitychange', onVisibility);
    };
  }, [user?.id, checkAuth]); // eslint-disable-line react-hooks/exhaustive-deps

  const login = async (email, password, role) => {
    const { data } = await api.post('/auth/login', { email, password, role });
    if (data.token) {
      localStorage.setItem('token', data.token);
    }
    localStorage.setItem('user', JSON.stringify(data));
    setUser(data);
    return data;
  };

  const logout = async () => {
    try { await api.post('/auth/logout'); } catch {}
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    setUser(null);
  };

  return (
    <AuthContext.Provider value={{ user, loading, login, logout, checkAuth, setUser }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used within AuthProvider');
  return ctx;
}
